/**
 * Everything that has to be above the router, in one component.
 *
 * The order matters and is the only reason this is a file rather than four lines
 * in `main.tsx`:
 *
 *   `QueryClientProvider` → `ApiProvider` → `AuthProvider` → session → `RouterProvider`
 *
 * The session provider is **above** the router because the root route's 404 and
 * error components replace the root component rather than render inside it — a
 * provider mounted by the root route would not be there when they run, and both
 * of them read the principal.
 *
 * `src/test/render.tsx` mirrors this stack exactly, with a fixed principal in
 * place of the session query, which is what makes a component test a test of the
 * component and not of Better Auth.
 */

import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { ApiProvider } from './api/provider.js'
import type { ApiClient } from './api/client.js'
import type { PortalAuthClient } from './auth/client.js'
import { NO_SESSION, SessionProvider } from './auth/principal.js'
import { AuthProvider } from './auth/provider.js'
import { sessionQueryOptions } from './auth/session.js'

/**
 * The cache's defaults, in one place.
 *
 * `retry: 1` rather than TanStack's three: this API answers a refusal as a
 * `problem+json` that the hooks turn into an `ApiProblem`, and retrying a `403`
 * twice more is two more audit-log lines saying the same thing. One retry covers
 * the case retries are for — a request that never arrived.
 *
 * `refetchOnWindowFocus: false`: the portal's numbers change when somebody in the
 * studio does something, not when you alt-tab, and a page full of money
 * re-fetching on every focus is a page that flickers.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
      mutations: { retry: 0 },
    },
  })
}

export function AppProviders({
  queryClient,
  api,
  auth,
  children,
}: {
  readonly queryClient: QueryClient
  readonly api: ApiClient
  readonly auth: PortalAuthClient
  readonly children: ReactNode
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider client={api}>
        <AuthProvider client={auth}>
          <BrowserSessionProvider auth={auth}>{children}</BrowserSessionProvider>
        </AuthProvider>
      </ApiProvider>
    </QueryClientProvider>
  )
}

/**
 * The session, from the same cache entry the router's guards read.
 *
 * The router has already awaited it in `__root.tsx`'s `beforeLoad`, so this never
 * suspends in practice; `NO_SESSION` covers the first paint and a failed read,
 * both of which mean "signed out" and both of which the guards turn into `/login`.
 */
function BrowserSessionProvider({
  auth,
  children,
}: {
  readonly auth: PortalAuthClient
  readonly children: ReactNode
}) {
  const { data } = useQuery(sessionQueryOptions(auth))
  return <SessionProvider value={data ?? NO_SESSION}>{children}</SessionProvider>
}
